// @ts-nocheck
// ============================================================
// TV WATCHDOG — Fase 11.8
// Garantia de uptime 24/7 para TVs enterprise
// Detecta: UI congelada, memory growth, slides presos, FPS baixo
// ============================================================
import { obs } from '@/core/observability'

interface WatchdogConfig {
  frozenThresholdMs:   number   // ms sem atividade = UI frozen (default 30s)
  memoryWarningMB:     number   // heap warning threshold
  heartbeatIntervalMs: number   // check frequency (default 10s)
  maxSlideDurationMs:  number   // max time a slide should stay mounted
}

const DEFAULT_CONFIG: WatchdogConfig = {
  frozenThresholdMs:   30_000,
  memoryWarningMB:     400,
  heartbeatIntervalMs: 10_000,
  maxSlideDurationMs:  120_000,  // 2 min max for any slide
}

interface WatchdogState {
  lastActivity:       number
  lastSlideChange:    number
  currentSlide:       string | null
  stallCount:         number
  heapSamples:        number[]
  isRecovering:       boolean
}

let _timer:    ReturnType<typeof setInterval> | null = null
let _state:    WatchdogState = createInitialState()
let _config:   WatchdogConfig = DEFAULT_CONFIG
let _onStall:  (() => void) | null = null
let _onRecover: (() => void) | null = null

function createInitialState(): WatchdogState {
  return {
    lastActivity:    Date.now(),
    lastSlideChange: Date.now(),
    currentSlide:    null,
    stallCount:      0,
    heapSamples:     [],
    isRecovering:    false,
  }
}

// ─── Public API ───────────────────────────────────────────────
export function startTVWatchdog(
  config:    Partial<WatchdogConfig> = {},
  onStall?:  () => void,
  onRecover?: () => void,
): () => void {
  _config   = { ...DEFAULT_CONFIG, ...config }
  _onStall  = onStall ?? null
  _onRecover = onRecover ?? null
  _state    = createInitialState()

  if (_timer) clearInterval(_timer)
  _timer = setInterval(runWatchdogCheck, _config.heartbeatIntervalMs)

  obs.info('tv_watchdog', 'TV Watchdog started')

  return () => {
    if (_timer) { clearInterval(_timer); _timer = null }
    obs.info('tv_watchdog', 'TV Watchdog stopped')
  }
}

export function watchdogHeartbeat(): void {
  _state.lastActivity = Date.now()
  if (_state.isRecovering) {
    _state.isRecovering = false
    _state.stallCount   = 0
    _onRecover?.()
    obs.info('tv_watchdog', 'TV recovered from stall')
  }
}

export function watchdogSlideChange(slideId: string): void {
  _state.lastSlideChange = Date.now()
  _state.currentSlide    = slideId
  watchdogHeartbeat()
}

// ─── Check loop ───────────────────────────────────────────────
function runWatchdogCheck(): void {
  const now      = Date.now()
  const sinceAct = now - _state.lastActivity
  const sinceSlide = now - _state.lastSlideChange

  // Detect UI freeze
  if (sinceAct > _config.frozenThresholdMs && !_state.isRecovering) {
    _state.stallCount++
    _state.isRecovering = true
    obs.warn('tv_watchdog', 'TV UI appears frozen', {
      sinceActivityMs: sinceAct,
      stallCount:      _state.stallCount,
      currentSlide:    _state.currentSlide,
    })
    _onStall?.()
  }

  // Detect stuck slide
  if (sinceSlide > _config.maxSlideDurationMs) {
    obs.warn('tv_watchdog', 'Slide stuck too long', {
      slideId:      _state.currentSlide,
      durationMs:   sinceSlide,
    })
  }

  // Memory monitoring
  if (typeof performance !== 'undefined' && (performance as any).memory) {
    const mem    = (performance as any).memory as { usedJSHeapSize: number }
    const mb     = Math.round(mem.usedJSHeapSize / 1_048_576)
    _state.heapSamples.push(mb)
    if (_state.heapSamples.length > 12) _state.heapSamples.shift()  // keep 2min window

    if (mb > _config.memoryWarningMB) {
      obs.warn('tv_watchdog', `High memory usage: ${mb}MB`, { mb })
    }

    // Detect memory leak (monotonically increasing over 2min)
    if (_state.heapSamples.length >= 6) {
      const first = _state.heapSamples[0]!
      const last  = _state.heapSamples[_state.heapSamples.length - 1]!
      if (last - first > 50) {  // 50MB growth in 2 min = leak signal
        obs.error('tv_watchdog', 'Possible memory leak detected', {
          growthMB: last - first,
          currentMB: last,
        })
      }
    }
  }
}

export function getWatchdogStatus() {
  return {
    isRecovering:  _state.isRecovering,
    stallCount:    _state.stallCount,
    sinceActivityMs: Date.now() - _state.lastActivity,
    currentSlide:  _state.currentSlide,
    heapMB:        _state.heapSamples[_state.heapSamples.length - 1] ?? 0,
  }
}
